import Vue from 'vue'
import i18n from '@/i18n'
import Axios from 'axios'
import { packsSettings } from 'Utils/config/diy'
import accountActions from 'ServicesActions/account'
import { Notification } from 'Utils/Classes/Notification'
import { filterCardsByStatus, sortArrayByObjectKey, setDriveFolder } from 'Utils'

export const namespaced = true

export const state = {
  cards: null,
  freeSlots: 0,
  account: null,
  addingCard: false,
}

export const mutations = {
  SET_ACCOUNT(state, data) {
    state.account = data
    // console.log(state.account)
  },

  SET_CARDS(state, cards) {
    Vue.set(state, 'cards', cards)
  },

  SET_NEW_CARD(state, card) {
    state.cards.push(card)
  },

  SET_CLIENT_OLD_CARD_UPDATE(state, { oldCard, updatedCard }) {
    // console.log(oldCard, updatedCard)
    const newCardData = Object.assign(oldCard, updatedCard.data)
    oldCard = newCardData
  },

  SET_FREE_SLOTS(state, slots) {
    state.freeSlots = slots
  },
}

export const actions = {
  async load({ commit, dispatch }, id) {
    const accountInfo = await Axios.all([
      accountActions.get.account(id).self,
      accountActions.get.account(id).cards,
    ])

    if (!accountInfo || !accountInfo.some((res) => res.status >= 200 && res.status <= 299)) return

    // Setting up account data ⚙️
    const accountData = accountInfo[0].data
    // drive folder
    accountData.drive_folder = setDriveFolder(accountData.drive_folder)
    // account pack names are case sensitive ↓
    accountData.pack_name = accountData.pack_name
      ? accountData.pack_name.toLowerCase()
      : Object.keys(packsSettings)[0]
    // pack icon
    accountData.pack_icon = await packsSettings[accountData.pack_name].icon()
    commit('SET_ACCOUNT', accountData)
    commit('SET_CARDS', sortArrayByObjectKey(accountInfo[1].data, 'status_changed', true))
    // Calculate account free slots
    dispatch('loadAccountSlots')
    return accountInfo
  },

  async loadCards({ commit }, id) {
    try {
      const accountCards = await accountActions.get.account(id).cards
      commit('SET_CARDS', sortArrayByObjectKey(accountCards.data, 'status_changed', true))
    } catch (error) {
      return error
    }
  },

  async addCard({ commit, dispatch }, { accountId, cardId }) {
    const notification = new Notification()
    notification.timeout = 4000
    try {
      const newCard = await accountActions.add.card(accountId, cardId)
      notification.type = 'success'
      notification.message = i18n.t('ui.notifications.success.add_card', {
        action: newCard.data.title,
      })
      commit('SET_NEW_CARD', newCard.data)
      // re-load
      dispatch('loadAccountSlots')
      return newCard
    } catch (error) {
      if (error.response) {
        notification.type = 'error'
        notification.message = i18n.t('ui.notifications.error.add_card')
      } else {
        notification.message = i18n.t('ui.notifications.error.add_card')
      }
      return error
    } finally {
      notification.dispatch()
    }
  },

  updateClientOldCard(context, { cardId, updatedCard }) {
    if (!context.state?.cards) return
    const oldCard = context.getters.getCard(cardId)
    // updating card for future edition
    context.commit('SET_CLIENT_OLD_CARD_UPDATE', { oldCard, updatedCard })
    // re-load slots
    context.dispatch('loadAccountSlots')
  },

  loadAccountSlots({ commit, getters }) {
    const availableSlots = getters.getAvailableSlots
    commit('SET_FREE_SLOTS', availableSlots)
  },
}

export const getters = {
  getCard(state) {
    return (id) => {
      return state.cards.filter((card) => card.id == id)[0]
    }
  },

  getActivePackCards(state) {
    return state.cards?.filter((card) => card.status === 'active' && card.type === 'pack')
  },

  getCardsByStatus(state) {
    return (status) => filterCardsByStatus(state.cards, status)
  },

  getPackSlots(state) {
    return packsSettings[state.account?.pack_name]?.slots
  },

  getAvailableSlots(state, getters) {
    const slots = getters.getPackSlots

    const used = state.cards
      ?.filter((card) => card.status === 'active' && card.type === 'pack')
      .reduce((acc, card) => acc + card.slots, 0)
    const availableSlots = slots - used

    return availableSlots < 0 ? 0 : availableSlots
  },
}
